// Запекание mesh-space позиций и весов скина для прокси-кубов bodyVox,
// чтобы центры ячеек следовали анимации так же, как skinned inner mesh.

import {
  computeBoneMatricesInnerFromOuterPose,
  computeNodeWorldMatrices,
  type PoseSample,
} from "./animation";
import type { LoadedMesh, SubMesh } from "./meshLoader";
import {
  BODY_VOX_CUBE,
  BODY_VOX_OX,
  BODY_VOX_OY,
  BODY_VOX_OZ,
  BODY_VOX_SX,
  BODY_VOX_SY,
  BODY_VOX_SZ,
} from "./bodyVox";
import { mat4Multiply, type Mat4 } from "./math";

const CELL_COUNT = BODY_VOX_SX * BODY_VOX_SY * BODY_VOX_SZ;

export interface BodyVoxSkinBake {
  /** 1 — для этой ячейки есть референсная вершина (скин). */
  cellFilled: Uint8Array;
  /** mesh-space позиция (как aPos), длина CELL_COUNT * 3. */
  meshPos: Float32Array;
  weights: Float32Array;
  joints: Uint16Array;
}

const emptyPose: PoseSample = {
  translation: new Map(),
  rotation: new Map(),
  scale: new Map(),
};

function innerNodeNameLower(inner: LoadedMesh, sm: SubMesh): string {
  const n = inner.nodes[sm.nodeIdx];
  return (n?.name ?? "").toLowerCase();
}

function innerSubMeshIsVoxelCarcass(inner: LoadedMesh, sm: SubMesh): boolean {
  if (sm.bodyLayer === "inner") return true;
  const low = innerNodeNameLower(inner, sm);
  return low.includes("voxel") || low.includes("carcass") || low.includes("blocks");
}

function innerMeshHasVoxelLike(inner: LoadedMesh): boolean {
  for (const sm of inner.subMeshes) {
    if (innerSubMeshIsVoxelCarcass(inner, sm)) return true;
  }
  return false;
}

/** Индексы ячейки тела по локали тела (как mesh.vert bodyVoxelCellIndices). */
function cellIndicesFromBodyLocal(lx: number, ly: number, lz: number): [number, number, number] {
  const cs = BODY_VOX_CUBE;
  const ox = BODY_VOX_OX;
  const oy = BODY_VOX_OY;
  const oz = BODY_VOX_OZ;
  const gx = (lx - ox) / cs + 1e-4;
  const gy = (ly - oy) / cs + 1e-4;
  const gz = (lz - oz) / cs + 1e-4;
  return [Math.floor(gx), Math.floor(gy), Math.floor(gz)];
}

function cellIdxFromIJK(ix: number, iy: number, iz: number): number {
  return ix + iy * BODY_VOX_SX + iz * BODY_VOX_SX * BODY_VOX_SY;
}

/** Референсная model-матрица инстанса (как в renderer для inner draw). */
export function buildBodyVoxInstModel(
  out: Mat4,
  x: number,
  y: number,
  z: number,
  yaw: number,
  yawOffset: number,
  scale: number,
): void {
  const finalYaw = yaw + yawOffset;
  const c = Math.cos(finalYaw);
  const s = Math.sin(finalYaw);
  const sc = scale;
  out[0] = sc * c;
  out[1] = sc * s;
  out[2] = 0;
  out[3] = 0;
  out[4] = 0;
  out[5] = 0;
  out[6] = sc;
  out[7] = 0;
  out[8] = sc * s;
  out[9] = -sc * c;
  out[10] = 0;
  out[11] = 0;
  out[12] = x;
  out[13] = y;
  out[14] = z;
  out[15] = 1;
}

function accumulateSkinMatrix(
  out: Mat4,
  bones: Float32Array,
  ji: Uint16Array,
  wi: Float32Array,
  slotBase: number,
): void {
  out.fill(0);
  for (let k = 0; k < 4; k++) {
    const w = wi[slotBase + k];
    if (w < 1e-8) continue;
    const j = ji[slotBase + k]!;
    const B = bones.subarray(j * 16, j * 16 + 16);
    for (let c = 0; c < 4; c++) {
      for (let r = 0; r < 4; r++) {
        out[c * 4 + r] += w * B[c * 4 + r];
      }
    }
  }
}

function transformMat4Vec3(out: Float32Array, m: Mat4, x: number, y: number, z: number): void {
  out[0] = m[0] * x + m[4] * y + m[8] * z + m[12];
  out[1] = m[1] * x + m[5] * y + m[9] * z + m[13];
  out[2] = m[2] * x + m[6] * y + m[10] * z + m[14];
}

const _tmpCombo = new Float32Array(16);

/**
 * Мировая позиция центра ячейки: uModel * Σ w_i B_i * p_mesh (как mesh.vert).
 * false — ячейка не в bake, caller делает rigid-fallback.
 */
export function worldPosForBodyVoxProxyCell(
  bake: BodyVoxSkinBake,
  cellIdx: number,
  innerBones: Float32Array,
  instModel: Mat4,
  skinScratch: Mat4,
  out: Float32Array,
): boolean {
  if (!bake.cellFilled[cellIdx]!) return false;
  const pb = cellIdx * 3;
  const jb = cellIdx * 4;
  const px = bake.meshPos[pb]!;
  const py = bake.meshPos[pb + 1]!;
  const pz = bake.meshPos[pb + 2]!;
  accumulateSkinMatrix(skinScratch, innerBones, bake.joints, bake.weights, jb);
  mat4Multiply(_tmpCombo, instModel, skinScratch);
  transformMat4Vec3(out, _tmpCombo, px, py, pz);
  return true;
}

/** Жёсткая привязка к актёру — только uActorYaw (как mesh.vert bodyLocalFromWorld). */
export function rigidBodyLocalToWorld(
  instX: number,
  instY: number,
  instZ: number,
  actorYaw: number,
  lx: number,
  ly: number,
  lz: number,
  out: Float32Array,
): void {
  const cs = Math.cos(actorYaw);
  const sn = Math.sin(actorYaw);
  out[0] = instX + (lx * cs - ly * sn);
  out[1] = instY + (lx * sn + ly * cs);
  out[2] = instZ + lz;
}

/**
 * Одна вершина inner voxel mesh на ячейку (rest pose outer → inner bones).
 * Вызывать один раз на пару (outer, inner) при создании инстанса.
 */
export function bakeBodyVoxProxyData(
  inner: LoadedMesh,
  outer: LoadedMesh,
  yawOffset: number,
): BodyVoxSkinBake | null {
  if (!inner.skin || !outer.nodes.length) return null;

  const outerNW = new Float32Array(outer.nodes.length * 16);
  computeNodeWorldMatrices(outer.nodes, outer.nodeTraversalOrder, emptyPose, outerNW);

  const innerBones = new Float32Array(inner.skin.joints.length * 16);
  computeBoneMatricesInnerFromOuterPose(
    inner.skin,
    inner.nodes,
    outer.nodes,
    outerNW,
    innerBones,
  );

  const im = new Float32Array(16);
  buildBodyVoxInstModel(im, 0, 0, 0, 0, yawOffset, 1);

  const skinM = new Float32Array(16);
  const combo = new Float32Array(16);
  const wp = new Float32Array(3);

  const cellFilled = new Uint8Array(CELL_COUNT);
  const meshPos = new Float32Array(CELL_COUNT * 3);
  const weights = new Float32Array(CELL_COUNT * 4);
  const joints = new Uint16Array(CELL_COUNT * 4);

  const voxelLike = innerMeshHasVoxelLike(inner);

  for (const sm of inner.subMeshes) {
    if (innerSubMeshIsDuplicateWeapon(inner, sm)) continue;
    if (innerSubMeshIsOuterShell(inner, sm)) continue;
    if (voxelLike && !innerSubMeshIsVoxelCarcass(inner, sm)) continue;
    if (!sm.hasSkin || !sm.cpuVerts.joints || !sm.cpuVerts.weights) continue;

    const pos = sm.cpuVerts.positions;
    const jt = sm.cpuVerts.joints;
    const wt = sm.cpuVerts.weights;
    const nVert = pos.length / 3;

    for (let vi = 0; vi < nVert; vi++) {
      const jBase = vi * 4;
      const pBase = vi * 3;
      const ws = wt[jBase]! + wt[jBase + 1]! + wt[jBase + 2]! + wt[jBase + 3]!;
      if (ws < 1e-8) continue;

      accumulateSkinMatrix(skinM, innerBones, jt, wt, jBase);
      mat4Multiply(combo, im, skinM);
      const ax = pos[pBase]!;
      const ay = pos[pBase + 1]!;
      const az = pos[pBase + 2]!;
      transformMat4Vec3(wp, combo, ax, ay, az);

      // bodyLocalFromWorld при actor (0,0), yaw 0, footZ 0 → lx=wp.x, ly=wp.y, lz=wp.z
      const [ix, iy, iz] = cellIndicesFromBodyLocal(wp[0]!, wp[1]!, wp[2]!);
      if (ix < 0 || iy < 0 || iz < 0) continue;
      if (ix >= BODY_VOX_SX || iy >= BODY_VOX_SY || iz >= BODY_VOX_SZ) continue;

      const ci = cellIdxFromIJK(ix, iy, iz);
      if (cellFilled[ci]) continue;

      cellFilled[ci] = 1;
      const mb = ci * 3;
      meshPos[mb] = ax;
      meshPos[mb + 1] = ay;
      meshPos[mb + 2] = az;
      const wb = ci * 4;
      for (let k = 0; k < 4; k++) {
        weights[wb + k] = wt[jBase + k]!;
        joints[wb + k] = jt[jBase + k]!;
      }
    }
  }

  for (let i = 0; i < cellFilled.length; i++) {
    if (cellFilled[i]) {
      return { cellFilled, meshPos, weights, joints };
    }
  }
  return null;
}

/** Дубликат логики renderer: не запекаем гладкий outer shell. */
function innerSubMeshIsOuterShell(inner: LoadedMesh, sm: SubMesh): boolean {
  if (sm.bodyLayer === "outer") return true;
  const low = innerNodeNameLower(inner, sm);
  if (low === "output") return true;
  if (low.includes("smooth") || low.includes("shell")) return true;
  if (low.includes("outer") && !low.includes("voxel")) return true;
  return false;
}

function innerSubMeshIsDuplicateWeapon(inner: LoadedMesh, sm: SubMesh): boolean {
  const low = innerNodeNameLower(inner, sm);
  return low === "weapon" || low.includes("weapon");
}
